/**
 * ResumeHero — "Continuá donde quedaste": unifica el audiolibro en curso
 * (Escuchar) y la unidad de Programa en progreso en una sola card contextual.
 *
 *   • mode 'both'    → Escuchar protagonista + Programa como fila secundaria
 *   • mode 'listen'  → sólo el audiolibro en curso
 *   • mode 'program' → sólo la unidad de Programa
 *   • mode 'empty'   → no se renderiza (los modos toman protagonismo)
 *
 * `useHomeResume()` deriva el modo del estado REAL. Para Escuchar usa DOS
 * fuentes: el capítulo abierto en esta sesión (useListen, con pos/duration en
 * vivo) y —si no hay ninguno abierto, p. ej. tras recargar— el más reciente en
 * progreso del backend (`GET /listen/progress`, ordenado por recencia) resuelto
 * contra el catálogo. Así el hero muestra el audiolibro aunque no lo hayas
 * abierto en esta sesión. Para Programa usa la unidad `in_progress` (useProgram).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useListen } from '../../listen/ListenProvider.js';
import { bookEditions, bookKeypoints } from '../../listen/bookLib.js';
import { pickListenResume } from '../../listen/resume.js';
import { fmtClock } from '../../listen/listenUi.js';
import { getCatalog, getProgress } from '../../listen/listenApi.js';
import type { CatalogBook, CatalogKeypoint, Edition } from '../../listen/types.js';
import { useProgram } from '../../program/useProgram.js';
import type { ProgramRecommendation } from '../../../lib/programApi.js';
import { createSessionCache } from '../../../lib/sessionCache.js';
import { IconArrowRight, IconCap, IconPause, IconPlay, IconSpark } from '../icons.js';

const CYAN = '#5ce1e6';
const CORAL = '#ff9d6c';

export type ResumeMode = 'both' | 'listen' | 'program' | 'empty';

/** Audiolibro a retomar (en sesión o derivado del backend). */
export interface ResumeListen {
  book: CatalogBook;
  chapter: CatalogKeypoint;
  edition: Edition;
  posSec: number;
  durationSec: number;
  playing: boolean;
  /** `true` = abierto en esta sesión (audio cargado); `false` = derivado del backend. */
  live: boolean;
}

export interface ResumeUnit {
  id: string;
  title: string;
  cefr: string;
  n: number;
  mastered: number;
  total: number;
}

/** Libro sugerido para arrancar (Home v2, estado vacío de la card Escuchar). */
export interface SuggestedListen {
  book: CatalogBook;
  chapter: CatalogKeypoint;
  edition: Edition;
}

export interface HomeResume {
  mode: ResumeMode;
  listen: ResumeListen | null;
  unit: ResumeUnit | null;
  /**
   * Estados vacíos ricos del Home v2 (no afectan `mode`, que sigue mandando
   * en el ResumeHero de desktop): sin unidad in_progress → la recomendada
   * por el servidor; sin audiolibro a retomar → el primer libro del
   * catálogo como sugerencia "empezá por acá".
   */
  nextUnit: ResumeUnit | null;
  suggestedListen: SuggestedListen | null;
  /**
   * Recomendaciones "Para vos" del snapshot del Programa (mismo `GET /program`
   * que `unit`/`nextUnit` — NO un fetch aparte). Vienen resueltas junto con
   * `resolved`, así el pie de la card Programa existe DESDE el reveal: si
   * llegara después, su aparición agranda la card y el reparto flex del
   * `.h2-body` encoge las otras dos (~8px) ya visibles — el "salto" del Home.
   */
  recommendations: ProgramRecommendation[];
  /**
   * `true` cuando el modo ya está asentado (Escuchar resuelto + Programa
   * cargado). El home espera esto para revelar el contenido de una sola vez y
   * no insertar el hero "de a bloques" (hold-and-reveal, ver HomeMobile).
   */
  resolved: boolean;
  /**
   * `true` si el fetch del progreso/catálogo falló por red — distinto de "no
   * hay nada que retomar". La card de Escuchar lo usa para ofrecer reintento en
   * vez de caer al estado vacío silencioso.
   */
  listenError: boolean;
  /** Reintenta traer el progreso del backend (card de Escuchar). */
  retryListen: () => void;
  /** Carga el capítulo si vino del backend (no estaba en sesión); el caller navega. */
  startListen: () => void;
}

// Caché de sesión (stale-while-revalidate) del audiolibro a retomar derivado
// del backend: una revisita al Home monta resuelta al instante; el fetch de
// fondo revalida. Se limpia al cambiar de usuario (AuthProvider).
const listenResumeCache = createSessionCache<{
  backendListen: ResumeListen | null;
  suggestedListen: SuggestedListen | null;
}>();

/** Lee el estado real (Escuchar + Programa) y deriva el modo del hero. */
export function useHomeResume(): HomeResume {
  const L = useListen();
  const { snapshot, loading: programLoading } = useProgram();
  const cachedResume = listenResumeCache.get();
  const [backendListen, setBackendListen] = useState<ResumeListen | null>(
    cachedResume ? cachedResume.backendListen : null,
  );
  const [suggestedListen, setSuggestedListen] = useState<SuggestedListen | null>(
    cachedResume ? cachedResume.suggestedListen : null,
  );
  // ¿Terminó de resolverse el audiolibro a retomar? (sesión = inmediato; con
  // caché = inmediato; si no, tras el fetch al backend). Combinado con
  // `programLoading` define `resolved`, el gate del hold-and-reveal del home.
  const [listenResolved, setListenResolved] = useState(cachedResume !== null);
  // Distingue "falló la red al traer el progreso" de "no hay nada que retomar":
  // sin esto el catch hacía fail-open a vacío y la card de Escuchar caía a su
  // estado vacío sin avisar ni ofrecer reintento.
  const [listenError, setListenError] = useState(false);

  // Audiolibro abierto en ESTA sesión (pos/duration en vivo). durationSec es
  // la duración REAL del <audio> (0 hasta que carga metadata) — NUNCA el
  // durationTargetMin del catálogo, que es el objetivo del guion y puede
  // diferir mucho del mp3 real (la UI oculta el total mientras sea 0).
  const liveListen = useMemo<ResumeListen | null>(() => {
    if (!L.book || !L.chapter || !L.ref) return null;
    const durationSec = L.duration;
    return {
      book: L.book,
      chapter: L.chapter,
      edition: L.ref.edition,
      posSec: L.pos,
      durationSec,
      playing: L.playing,
      live: true,
    };
  }, [L.book, L.chapter, L.ref, L.duration, L.pos, L.playing]);

  const hasLive = liveListen != null;

  // Trae el más reciente en progreso del backend (resuelto contra el catálogo).
  // Reusable: lo llama el effect inicial y el reintento de la card de Escuchar.
  // Estable (deps []) → seguro como dep del effect y como `retryListen`.
  const loadBackendResume = useCallback(async (): Promise<void> => {
    setListenError(false);
    try {
      const [{ progress }, { books }] = await Promise.all([getProgress(), getCatalog()]);
      // Criterio compartido con el "Seguí escuchando" de la Audioteca
      // (`pickListenResume`): el más reciente con posición > 0.
      const target = pickListenResume(progress, books);
      const resolved: { backendListen: ResumeListen | null; suggestedListen: SuggestedListen | null } =
        target
          ? {
              backendListen: {
                book: target.book,
                chapter: target.chapter,
                edition: target.edition,
                posSec: target.entry.lastPositionSec,
                // Duración real del mp3 (rendition del lastCefr, del propio
                // `GET /listen/progress`). `null` = desconocida → 0, la
                // convención de la UI para "sin total, mostrar sólo la
                // posición" (misma que el <audio> antes de cargar metadata).
                durationSec: target.entry.durationSec ?? 0,
                playing: false,
                live: false,
              },
              suggestedListen: null,
            }
          : { backendListen: null, suggestedListen: deriveSuggestion(books) };
      listenResumeCache.set(resolved);
      setBackendListen(resolved.backendListen);
      setSuggestedListen(resolved.suggestedListen);
    } catch {
      // Distinto de "no hay nada que retomar": NO fail-open silencioso. La card
      // de Escuchar muestra un estado de reintento en vez de caer a "Explorar".
      setBackendListen(null);
      setListenError(true);
    } finally {
      setListenResolved(true);
    }
  }, []);

  // Con audiolibro en sesión, el modo ya está resuelto sin pegar al backend;
  // si no, traemos el progreso del backend.
  useEffect(() => {
    if (hasLive) {
      setListenResolved(true);
      return;
    }
    void loadBackendResume();
  }, [hasLive, loadBackendResume]);

  const unit = useMemo<ResumeUnit | null>(() => {
    if (!snapshot) return null;
    const u = snapshot.units.find((x) => x.status === 'in_progress');
    if (!u) return null;
    return {
      id: u.id,
      title: u.titleEs ?? u.title,
      cefr: u.cefr,
      n: snapshot.units.indexOf(u) + 1,
      mastered: u.subUnitsCompleted,
      total: u.subUnitsTotal,
    };
  }, [snapshot]);

  // Sin unidad empezada, respetamos la recomendación primaria del servidor
  // (también la unidad concreta de una familia). El catálogo sólo da la posición.
  const nextUnit = useMemo<ResumeUnit | null>(() => {
    if (!snapshot || unit) return null;
    const recommendedId = snapshot.recommendation?.unit?.id;
    const u = snapshot.units.find((x) => x.id === recommendedId);
    if (!u) return null;
    return {
      id: u.id,
      title: u.titleEs ?? u.title,
      cefr: u.cefr,
      n: snapshot.units.indexOf(u) + 1,
      mastered: u.subUnitsCompleted,
      total: u.subUnitsTotal,
    };
  }, [snapshot, unit]);

  const listen = liveListen ?? backendListen;

  const startListen = useCallback(() => {
    const r = liveListen ?? backendListen;
    if (!r || r.live) return; // en sesión → ya está cargado
    void L.openChapter(r.book, r.chapter, r.edition, { play: true });
  }, [liveListen, backendListen, L]);

  const mode: ResumeMode =
    listen && unit ? 'both' : listen ? 'listen' : unit ? 'program' : 'empty';

  // Asentado cuando Escuchar terminó de resolverse Y el Programa ya cargó (su
  // unidad in_progress define 'both' vs 'listen'): así el reveal del home no
  // muta el hero después de aparecer.
  const resolved = listenResolved && !programLoading;

  const recommendations = snapshot?.recommendations ?? [];

  return {
    mode,
    listen,
    unit,
    nextUnit,
    suggestedListen,
    recommendations,
    resolved,
    listenError,
    retryListen: () => void loadBackendResume(),
    startListen,
  };
}

/** Primer libro del catálogo con un capítulo abrible → sugerencia "empezá por acá". */
function deriveSuggestion(books: readonly CatalogBook[]): SuggestedListen | null {
  const book = books[0];
  if (!book) return null;
  const edition = bookEditions(book)[0];
  if (!edition) return null;
  const chapter = bookKeypoints(book, edition)[0];
  if (!chapter) return null;
  return { book, chapter, edition };
}

function bookInitials(title: string): string {
  return title
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();
}

function ResumeCover({ title, accent, coverUrl }: { title: string; accent: string; coverUrl: string | null }) {
  if (coverUrl) {
    return (
      <div className="resume-cover" style={{ backgroundImage: `url(${coverUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
    );
  }
  return (
    <div
      className="resume-cover"
      style={{ background: `linear-gradient(150deg, ${accent} 0%, ${accent}aa 55%, ${accent}66 100%)` }}
    >
      <svg className="cover-orbit" viewBox="0 0 60 60" fill="none" stroke="rgba(0,0,0,0.32)" strokeWidth="1.4">
        <ellipse cx="30" cy="30" rx="24" ry="11" transform="rotate(-28 30 30)" />
        <circle cx="48" cy="20" r="2.4" fill="rgba(0,0,0,0.4)" stroke="none" />
      </svg>
      <span className="cover-init">{bookInitials(title)}</span>
    </div>
  );
}

function ResumeListenBlock({
  listen,
  onOpen,
  onTogglePlay,
}: {
  listen: ResumeListen;
  onOpen: () => void;
  /** Play/pausa in-situ (no navega): si está cargado togglea, si no lo arranca. */
  onTogglePlay: () => void;
}) {
  const { book, chapter, posSec, durationSec, playing } = listen;
  const pct = durationSec > 0 ? Math.min(1, posSec / durationSec) : 0;
  const accent = book.accent ?? CYAN;
  return (
    <div className="resume-main" onClick={onOpen} role="button" tabIndex={0} style={{ cursor: 'pointer' }}>
      <ResumeCover title={book.title} accent={accent} coverUrl={book.coverUrl} />
      <div className="resume-info">
        <span className="resume-tag listen"><span className="dot" /> Escuchando</span>
        <div className="resume-title">{book.title}</div>
        <div className="resume-sub">Cap. {chapter.order} · {chapter.titleEs ?? chapter.title}</div>
        <div className="resume-progress">
          <div className="resume-bar"><i style={{ width: `${pct * 100}%`, background: CYAN }} /></div>
          <span className="resume-time">
            {fmtClock(posSec)}{durationSec > 0 ? ` / ${fmtClock(durationSec)}` : ''}
          </span>
        </div>
      </div>
      <button
        type="button"
        className="resume-play listen"
        onClick={(e) => { e.stopPropagation(); onTogglePlay(); }}
        aria-label={playing ? 'Pausar' : 'Reproducir'}
      >
        {playing ? <IconPause size={18} /> : <IconPlay size={18} />}
      </button>
    </div>
  );
}

function ResumeProgramBlock({ unit, onOpen }: { unit: ResumeUnit; onOpen: () => void }) {
  const pct = unit.total > 0 ? unit.mastered / unit.total : 0;
  return (
    <div className="resume-main" onClick={onOpen} role="button" tabIndex={0} style={{ cursor: 'pointer' }}>
      <div className="resume-prognode"><IconCap size={34} /></div>
      <div className="resume-info">
        <span className="resume-tag program"><span className="dot" /> En progreso</span>
        <div className="resume-title">{unit.title}</div>
        <div className="resume-sub">Cap. {unit.n} · {unit.cefr} · {unit.mastered} de {unit.total} subunidades</div>
        <div className="resume-progress">
          <div className="resume-bar"><i style={{ width: `${pct * 100}%`, background: CORAL }} /></div>
          <span className="resume-time">{Math.round(pct * 100)}%</span>
        </div>
      </div>
      <button
        type="button"
        className="resume-play program"
        onClick={(e) => { e.stopPropagation(); onOpen(); }}
        aria-label="Seguir el programa"
      >
        <IconPlay size={18} />
      </button>
    </div>
  );
}

function ResumeSecondaryProgram({ unit, onOpen }: { unit: ResumeUnit; onOpen: () => void }) {
  const pct = unit.total > 0 ? unit.mastered / unit.total : 0;
  return (
    <div className="resume-secondary" onClick={onOpen} role="button" tabIndex={0}>
      <span className="resume-sec-ic"><IconCap size={20} /></span>
      <div className="resume-sec-info">
        <div className="resume-sec-tag">Programa · también a medias</div>
        <div className="resume-sec-title">{unit.title}</div>
        <div className="resume-sec-prog">
          <div className="resume-sec-bar"><i style={{ width: `${pct * 100}%` }} /></div>
          <span className="resume-sec-meta">{unit.mastered} de {unit.total} subunidades</span>
        </div>
      </div>
      <span className="resume-sec-go"><IconArrowRight size={15} /></span>
    </div>
  );
}

interface ResumeHeroProps {
  resume: HomeResume;
  variant?: 'mobile' | 'desktop';
  onOpenListen: () => void;
  onOpenProgram: () => void;
}

export function ResumeHero({ resume, variant = 'mobile', onOpenListen, onOpenProgram }: ResumeHeroProps) {
  const { mode, listen, unit, startListen } = resume;
  const L = useListen();
  if (mode === 'empty') return null;

  // Play/pausa del audiolibro sin salir de la Home: si ya está cargado en sesión
  // (live) togglea el <audio> único; si vino del backend, lo arranca.
  const toggleListen = (): void => {
    if (!listen) return;
    if (listen.live) L.toggle();
    else startListen();
  };

  const isProgram = mode === 'program';
  const cardClass = [
    'card',
    'resume-card',
    variant === 'desktop' ? 'd-resume-card' : '',
    isProgram ? 'is-program' : 'is-listen',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={cardClass}>
      <div className="resume-eyebrow">
        <span className="ico" style={{ color: isProgram ? CORAL : CYAN }}><IconSpark size={13} /></span>
        <span className="tx">Continuá donde quedaste</span>
        <span className="spacer" />
        <span className={`resume-src-pill ${isProgram ? 'program' : 'listen'}`}>{isProgram ? 'Programa' : 'Escuchar'}</span>
      </div>

      {isProgram && unit ? (
        <ResumeProgramBlock unit={unit} onOpen={onOpenProgram} />
      ) : (
        listen && <ResumeListenBlock listen={listen} onOpen={onOpenListen} onTogglePlay={toggleListen} />
      )}

      {mode === 'both' && unit && <ResumeSecondaryProgram unit={unit} onOpen={onOpenProgram} />}
    </div>
  );
}
